import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import AudioMessagesPlayer from './AudioMessagesPlayer';
import ImageGallery from './ImageGallery';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from "rehype-raw";
import './MessageComponents.css'
import { useCachedBlobUrl } from "../../Services/useBlobDownloader";
import { FileAttachment } from "./FileAttachment";

/**
 * ----------------------------
 * Incoming Messages (Assistant)
 * ----------------------------
 */

// 1. Assistant header message (intro message)
// (This one is left intact because its wrapper is separate.)
export const AssistantHeaderMessage = ({message}) => {
    return (
        <motion.div
            className="assistant-header"
            exit={{display: "none"}}
        >
            {message.type === 'bot' ? (
                <>
                    {message.replacebotlogo === 'yes' ? (
                        <img className="avatar" src={message.logo} alt={message.newassistant}/>
                    ) : (
                        <motion.div
                            className="avatar bot"
                            initial={{opacity: 0, scale: 0.5}}
                            animate={{opacity: 1, scale: 1}}
                            exit={{opacity: 0, scale: 0.5}}
                            transition={{type: 'spring', stiffness: 200, damping: 15, duration: 0.1}}
                        />
                    )}
                    <div className="assistant-info">
                        <div className={message.showbotbadge === 'yes' ? 'bot-info' : 'name'}>
                            {message.newassistant}
                        </div>
                        <div className="assistant-subtitle">{message.subscription}</div>
                    </div>
                </>
            ) : (
                <>
                    <img className="avatar" src={message.logo} alt={message.newassistant}/>
                    <div className="assistant-info">
                        <div className="name">{message.newassistant}</div>
                        <div className="assistant-subtitle">{message.subscription}</div>
                    </div>
                </>
            )}
        </motion.div>
    );
};

/**
 * For all media and HTML components, we now return only the internal markup
 * that goes inside the message-bubble div.
 */

// 2. Media message from bot – URL
export const MediaMessageBotURL = ({message}) => {
    return (
        <>
            <p dangerouslySetInnerHTML={{__html: message.text}}></p>
            <a className='link-wrapper' href={message.url} target="_blank" rel="noopener noreferrer">
                <div className='link-container'>
                    <img className='link-image' src={message.image} alt={message.url}/>
                    <div className='link-description'>
                        <span className='description-title'>{message.title}</span>
                        <div className='description-text'>{message.description}</div>
                    </div>
                </div>
            </a>
        </>
    );
};

// 3. Media message from bot – Image (single or gallery)
export const MediaMessageBotImage = ({ message }) => {
  return (
    <>
      <ImageGallery message={message} />
      {message.text && (
        <div className='gallery-message'>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer">
                    {props.children}
                  </a>
                )
              }}
            >
                {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
            </ReactMarkdown>
        </div>
       )}
    </>
  );
};

export const MediaMessageBotAudio = ({ message }) => {
  const { getCachedBlobUrl } = useCachedBlobUrl();

  const key = message?.audio?.[0]?.key || null;
  const [audioUrl, setAudioUrl] = useState();

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();

    (async () => {
      try {
        if (!key || !getCachedBlobUrl) {
          if (!cancelled) setAudioUrl(null);
          return;
        }
        const blobUrl = await getCachedBlobUrl(key, "audio", { signal: ac.signal });
        if (!cancelled) setAudioUrl(blobUrl);
      } catch (e) {
        if (!cancelled) setAudioUrl(null);
        console.error("Failed to get blob url for audio", e);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [key, getCachedBlobUrl]);

  return (
    <>
      <AudioMessagesPlayer
        audioSrc={audioUrl}
        histogramData={message?.audio?.[0]?.histogram || []}
        playbackTime={message?.audio?.[0]?.playback_time}
      />
      {message?.text && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            ),
          }}
        >
          {typeof message.text === "string"
            ? message.text
            : JSON.stringify(message.text, null, 2)}
        </ReactMarkdown>
      )}
    </>
  );
};

export const MediaMessageBotFile = ({ message }) => {
  const { getCachedBlobUrl } = useCachedBlobUrl();
  const [hrefMap, setHrefMap] = useState({});

  const files = Array.isArray(message?.files) ? message.files : [];
  const filesFingerprint = React.useMemo(
    () => JSON.stringify(files.map(f => f?.key ?? "")),
    [files]
  );

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      if (!files.length || !getCachedBlobUrl) {
        if (alive) setHrefMap({});
        return;
      }
      try {
        const pairs = await Promise.all(
          files.map(async (file) => {
            const k = file?.key;
            if (!k) return [k, undefined];
            try {
              const blobUrl = await getCachedBlobUrl(k, "file", { signal: ac.signal });
              return [k, blobUrl || undefined];
            } catch {
              return [k, undefined];
            }
          })
        );
        if (!alive) return;
        setHrefMap(Object.fromEntries(pairs.filter(([k]) => k)));
      } catch (e) {
        if (alive) {
          console.error("Failed to build hrefMap", e);
          setHrefMap({});
        }
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [filesFingerprint, getCachedBlobUrl]);

  return (
    <>
      {message.images && message.images.length > 0 && <ImageGallery message={message} />}

      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === "string" ? message.text : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>

      {files.map((f, idx) => {
        const fileName  = f.fileName || "unknown";
        const size      = f.size || "";
        const key       = f.key;
        const extension = (f.extension || "").toLowerCase();
        const href      = hrefMap[key];
        const isLoading = !!key && !href; // є key, але ще немає blobUrl

        return (
          <div key={idx} className={`file-wrapper file-${extension}`}>
            {/* або СПІНЕР, або КНОПКА-ЛІНК */}
            {key && (
              isLoading ? (
                <div
                  className="is-loading"
                  aria-busy="true"
                  aria-disabled="true"
                  title="Loading…"
                  style={{
                    pointerEvents: "none",
                    right: "-20px",
                    position: "absolute",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 50 50"
                    style={{
                      position: "absolute",
                      pointerEvents: "none",
                    }}
                  >
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="31.4 188.4"
                    >
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 25 25"
                        to="360 25 25"
                        dur="0.9s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                </div>
              ) : (
                <a
                  className="file-download-link"
                  href={href}
                  download={fileName}
                  aria-label="Download file"
                />
              )
            )}

            <div className="file-info">
              <div className="file-name">{fileName}</div>
              <div className="file-creds">
                <span className="file-ext">{extension.toUpperCase()}</span>
                <span> Document</span>
                <span className="file-separator" />
                {size && (
                  <>
                    <span className="file-size">{size}</span>
                    <span> MB</span>
                  </>
                )}
              </div>
            </div>

            <div className="file-icon-wrapper">
              <div className="file-icon" />
            </div>
          </div>
        );
      })}
    </>
  );
};

// 6. Media message from bot – Video
export const MediaMessageBotVideo = ({message}) => {
    return (
        <>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    a: ({node, ...props}) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                        </a>
                    )
                }}
            >
                {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
            </ReactMarkdown>
            <video controls src={message.url}/>
            <div className="playback-time">Duration: {message.playbackTime}s</div>
            <span className="timestamp">{message.timestamp}</span>
        </>
    );
};

// 7. Media message from bot – HTML
export const MediaMessageBotHTML = ({message}) => {
    return (
        <>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    a: ({node, ...props}) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                        </a>
                    )
                }}
            >
                {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
            </ReactMarkdown>
            <div className="html-content" dangerouslySetInnerHTML={{__html: message.code}}/>
            <span className="timestamp">{message.timestamp}</span>
        </>
    );
};

/**
 * ----------------------------
 * Outbound Messages (User)
 * ----------------------------
 */

// 8. Media message from user – URL
export const MediaMessageUserURL = ({message}) => {
    return (
        <>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    a: ({node, ...props}) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                        </a>
                    )
                }}
            >
                {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
            </ReactMarkdown>
            <a className='link-wrapper' href={message.url} target="_blank" rel="noopener noreferrer">
                <div className='link-container'>
                    <img className='link-image' src={message.image} alt={message.url}/>
                    <div className='link-description'>
                        {message.title && (<span className='description-title'>{message.title}</span>)}
                        {message.description && (<div className='description-text'>
                            <div className='text-container'>{message.description}</div>
                        </div>)}
                    </div>
                </div>
            </a>
        </>
    );
};

// 9. Media message from user – Image (single or gallery)
export const MediaMessageUserImage = ({message}) => {
    return (
        <>
            <ImageGallery message={message}/>
            {message.text && (
                <div className='gallery-message'>
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                            a: ({node, ...props}) => (
                                <a {...props} target="_blank" rel="noopener noreferrer">
                                    {props.children}
                                </a>
                            )
                        }}
                    >
                        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
                    </ReactMarkdown>
                </div>
            )}
        </>
    );
};

// 10. Media message from user – Audio
export const MediaMessageUserAudio = ({ message }) => {
  const hasAudioArray = Array.isArray(message.audio) && message.audio.length > 0;
  const audioItem = hasAudioArray ? message.audio[0] : null;

  const hasText = !!message.text;

  if (!hasAudioArray && !hasText) {
    return null;
  }

  return (
    <>
      {hasAudioArray && audioItem && (
        <AudioMessagesPlayer
          audioKey={audioItem.key}
          histogramData={audioItem.histogram || []}
          playbackTime={audioItem.playback_time}
        />
      )}

      {hasText && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            ),
          }}
        >
          {typeof message.text === "string"
            ? message.text
            : JSON.stringify(message.text, null, 2)}
        </ReactMarkdown>
      )}
    </>
  );
};

// 11. Media message from user – File
export const MediaMessageUserFile = ({ message }) => {
  const files = Array.isArray(message.files) ? message.files : [];

  return (
    <>
      {message.images && message.images.length > 0 && (
        <ImageGallery message={message} />
      )}

      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {props.children}
            </a>
          ),
        }}
      >
        {typeof message.text === "string"
          ? message.text
          : JSON.stringify(message.text, null, 2)}
      </ReactMarkdown>

      {files.map((file, idx) => (
        <FileAttachment key={file.key || idx} file={file} />
      ))}
    </>
  );
};


// 12. Media message from user – Video
export const MediaMessageUserVideo = ({message}) => {
    return (
        <>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    a: ({node, ...props}) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                        </a>
                    )
                }}
            >
                {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
            </ReactMarkdown>
            <video controls src={message.url}/>
            <div className="playback-time">Duration: {message.playbackTime}s</div>
            <span className="timestamp">{message.timestamp}</span>
        </>
    );
};

// 13. Media message from user – HTML
export const MediaMessageUserHTML = ({message}) => {
    return (
        <>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    a: ({node, ...props}) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                        </a>
                    )
                }}
            >
                {typeof message.text === 'string' ? message.text : JSON.stringify(message.text, null, 2)}
            </ReactMarkdown>
            <div className="html-content" dangerouslySetInnerHTML={{__html: message.code}}/>
            <span className="timestamp">{message.timestamp}</span>
        </>
    );
};